import { useState } from 'react';
import { toast } from 'react-toastify';
import { getServiceRequestByCommonIdUserIdRole } from 'src/services/client';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  LegacyCommonAreaServiceRequest,
  LegacyCommonAreaServiceRequestEnvelope,
  normalizeCommonAreaServiceRequest,
} from 'src/pages/CommonArea/CustomHooks/CommonAreaServiceRequests/useCommonAreaServiceRequestsApi/helpers';

/* Hook seam around the legacy BOS `ServiceRequestByCommonArea` endpoint.
 *
 * Mirrors useItemServiceRequestsApi / useContactServiceRequestsApi /
 * useSuiteServiceRequestsApi. Normalizes the legacy row shape into
 * IServiceRequest so the shared dashboard table renders unchanged.
 *
 * TODO: switching this to `getDashboardServiceRequests({...})` is further
 * off than the Item case — IDashboardFilters does not currently expose a
 * CommonAreaId filter. Would need a backend change before we can drop the legacy seam. Out of scope here. */

export interface UseCommonAreaServiceRequestsApiArgs {
  commonAreaId: string;
  role: string;
}

export interface UseCommonAreaServiceRequestsApiReturn {
  serviceRequests: IServiceRequest[];
  loading: boolean;
  fetchCommonAreaServiceRequests: (
    args: UseCommonAreaServiceRequestsApiArgs
  ) => Promise<void>;
}

export const useCommonAreaServiceRequestsApi =
  (): UseCommonAreaServiceRequestsApiReturn => {
    const [serviceRequests, setServiceRequests] = useState<IServiceRequest[]>(
      []
    );
    const [loading, setLoading] = useState<boolean>(true);

    const fetchCommonAreaServiceRequests = async ({
      commonAreaId,
      role,
    }: UseCommonAreaServiceRequestsApiArgs): Promise<void> => {
      setLoading(true);

      const res: any = await getServiceRequestByCommonIdUserIdRole(
        commonAreaId,
        role,
        1,
        ''
      );

      if (res?.ok || res?.status === 200) {
        const json: LegacyCommonAreaServiceRequestEnvelope = await res.json();
        const rawResults: LegacyCommonAreaServiceRequest[] =
          json?.serviceRequestsList?.results ?? [];

        const normalized: IServiceRequest[] = rawResults.map(
          normalizeCommonAreaServiceRequest
        );

        setServiceRequests(normalized);
      } else {
        toast.error(
          'Failed to fetch service requests, please try again later.',
          {
            autoClose: 5000,
            hideProgressBar: true,
            position: 'top-center',
          }
        );
      }

      setLoading(false);
    };

    return {
      serviceRequests,
      loading,
      fetchCommonAreaServiceRequests,
    };
  };
